/**
 * WordPress AJAX helper hook.
 *
 * Wraps wp_ajax_* calls using FormData + fetch.
 * Expects WP's standard { success, data } response envelope.
 */

import { useState, useCallback } from 'react';
import type { WPAjaxResponse } from '../types';

interface UseAjaxOptions {
  ajaxUrl: string;
  nonce: string;
}

interface AjaxState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useAjax<T>(options: UseAjaxOptions) {
  const [state, setState] = useState<AjaxState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const call = useCallback(
    async (action: string, params: Record<string, unknown> = {}): Promise<T | null> => {
      setState({ data: null, loading: true, error: null });

      const body = new FormData();
      body.append('action', action);
      body.append('nonce', options.nonce);

      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined) continue;
        if (typeof value === 'object') {
          body.append(key, JSON.stringify(value));
        } else {
          body.append(key, String(value));
        }
      }

      try {
        const res = await fetch(options.ajaxUrl, { method: 'POST', body });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const json: WPAjaxResponse<T> = await res.json();

        if (!json.success) {
          const msg = (json as { success: false; data: { message: string } }).data.message;
          setState({ data: null, loading: false, error: msg });
          return null;
        }

        setState({ data: json.data, loading: false, error: null });
        return json.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Onbekende fout';
        setState({ data: null, loading: false, error: msg });
        return null;
      }
    },
    [options.ajaxUrl, options.nonce]
  );

  return { ...state, call };
}

/**
 * Upload a file to a WP AJAX action.
 * Adds extra FormData fields alongside the file.
 */
export async function uploadFoto(
  ajaxUrl: string,
  nonce: string,
  action: string,
  file: File,
  extraFields: Record<string, string | number>
): Promise<WPAjaxResponse<unknown>> {
  const body = new FormData();
  body.append('action', action);
  body.append('nonce', nonce);
  body.append('foto', file);
  for (const [k, v] of Object.entries(extraFields)) {
    body.append(k, String(v));
  }
  const res = await fetch(ajaxUrl, { method: 'POST', body });
  return res.json();
}
